/**
 * The bullet bar: the specification range (outer, amber) with the control limits (inner, green) and the latest value
 * marker, drawn over a scale. Geometry only — every value is the caller's (the plan's or the server's). With no
 * scale given, the bar spans the values it is handed.
 *
 * A one-sided specification (Q4j) reads as one: "≤ USL" (NMT) is drawn from its natural lower bound — zero, when no
 * value handed is below it — up to the USL; "≥ LSL" (NLT) is the mirror, from the LSL up past the largest value handed
 * by as much again (the bar's open end, faded, says no upper limit). With nothing else to place, the limit sits at its own end of the
 * bar, never in the middle of it.
 */
import React from 'react';

const has = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const PAD = 4; // % kept clear at each end so a marker on the edge stays visible
const NATURAL_LOWER = 0; // the lower bound of a "≤ USL" scale when every value is non-negative (an impurity, a loss, a count)

export default function LimitsBar({ spec = {}, control = {}, value, scale, tone, text, ticks = [], testId }) {
  const drawnTicks = ticks.filter((t) => has(t.at));
  const nums = [spec.lo, spec.hi, control.lo, control.hi, value, ...drawnTicks.map((t) => t.at)].filter(has).map(Number);
  const upperOnly = has(spec.hi) && !has(spec.lo);   // NMT
  const lowerOnly = has(spec.lo) && !has(spec.hi);   // NLT
  const lowest = nums.length ? Math.min(...nums) : null;
  const naturalMin = upperOnly && lowest !== null && lowest >= NATURAL_LOWER && Number(spec.hi) > NATURAL_LOWER ? NATURAL_LOWER : lowest;
  const min = has(scale?.min) ? Number(scale.min) : naturalMin;
  // NLT: the open end lies as far past the largest value as that value lies past the LSL, so it reads mid-bar
  const largest = nums.length ? Math.max(...nums) : null;
  const naturalMax = lowerOnly && largest !== null && largest > Number(spec.lo) ? largest + (largest - Number(spec.lo)) : largest;
  const max = has(scale?.max) ? Number(scale.max) : naturalMax;
  const span = max - min;
  // nothing but the limit to place: a one-sided limit sits at its own end, a lone value in the middle
  const flatAt = upperOnly ? 100 - PAD : lowerOnly ? PAD : 50;
  const pos = (v) => {
    if (!nums.length) return null;
    const p = span ? PAD + ((Number(v) - min) / span) * (100 - 2 * PAD) : flatAt;
    return Math.min(100, Math.max(0, p));
  };
  const seg = (lo, hi) => {
    if (!has(lo) && !has(hi)) return null;
    const a = has(lo) ? pos(lo) : 0; const b = has(hi) ? pos(hi) : 100;
    return { left: `${Math.min(a, b)}%`, width: `${Math.max(Math.abs(b - a), 1)}%` };
  };
  const specSeg = seg(spec.lo, spec.hi);
  const ctlSeg = seg(control.lo, control.hi);
  return (
    <div className={`cu-lbar${lowerOnly ? ' cu-lbar--open-hi' : ''}`} data-testid={testId}>
      <div className="cu-lbar-track" aria-hidden>
        {specSeg && <span className="cu-lbar-spec" style={specSeg} />}
        {ctlSeg && <span className="cu-lbar-control" style={ctlSeg} />}
        {drawnTicks.map((t, i) => <span key={i} className={`cu-lbar-tick cu-tone--${t.tone || 'neutral'}`} style={{ left: `${pos(t.at)}%` }} />)}
        {has(value) && nums.length > 0 && <span className={`cu-lbar-mark${tone ? ` cu-tone--${tone}` : ''}`} style={{ left: `${pos(value)}%` }} />}
      </div>
      {text ? <div className="cu-lbar-text">{text}</div> : null}
    </div>
  );
}
